import { DOCUMENT, Location } from '@angular/common'
import {
  EnvironmentProviders,
  inject,
  provideAppInitializer,
} from '@angular/core'
import { NavigationError, Router } from '@angular/router'
import { filter } from 'rxjs'

/** Messages thrown by webpack and by native dynamic imports (Chrome, Safari, Firefox) */
const CHUNK_LOAD_ERROR =
  /ChunkLoadError|Loading chunk [\w-]+ failed|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i

const LAST_RELOAD_KEY = 'wm-chunk-load-reload-at'
/** A chunk still missing right after a reload is a real outage: don't loop */
const RELOAD_COOLDOWN_MS = 10_000

export function isChunkLoadError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? `${error.name} ${error.message}`
      : String(error ?? '')
  return CHUNK_LOAD_ERROR.test(message)
}

/**
 * Fully loads `url`, which fetches the latest index.html and its chunks.
 * Returns false when a reload already happened within the cooldown, or when the
 * attempt cannot be recorded: without that record nothing would stop a reload loop.
 */
export function reloadOnce(url: string, win: Window): boolean {
  try {
    const lastReload = Number(win.sessionStorage.getItem(LAST_RELOAD_KEY))
    if (Date.now() - lastReload < RELOAD_COOLDOWN_MS) {
      return false
    }
    win.sessionStorage.setItem(LAST_RELOAD_KEY, String(Date.now()))
  } catch {
    return false
  }
  win.location.assign(url)
  return true
}

/**
 * A tab opened before a deploy runs the previous build: navigating to a lazy route
 * then requests a chunk that may no longer exist. Reload on the targeted url instead
 * of leaving the user on a broken navigation.
 */
export function provideChunkLoadRecovery(): EnvironmentProviders {
  return provideAppInitializer(() => {
    const router = inject(Router)
    const location = inject(Location)
    const win = inject(DOCUMENT).defaultView

    router.events
      .pipe(
        filter(
          (event): event is NavigationError =>
            event instanceof NavigationError && isChunkLoadError(event.error)
        )
      )
      .subscribe((event) => {
        if (win) {
          reloadOnce(location.prepareExternalUrl(event.url), win)
        }
      })
  })
}
